import React, { useEffect, useState } from 'react';
import { DragDropContext, Droppable, Draggable } from '@hello-pangea/dnd';
import api from '../../services/api';
import toast, { Toaster } from 'react-hot-toast';
import { 
    Plus, Trash2, Settings2, Info, ListPlus, 
    ChevronDown, X, Calculator, HelpCircle, GripVertical,
    AlertTriangle 
} from 'lucide-react';
import '../../styles/SubComponentsAdmin.css';

const Adicionais = ({ estabelecimentoId }) => {
    const [grupos, setGrupos] = useState([]);
    const [loading, setLoading] = useState(true);
    const [showModal, setShowModal] = useState(false);
    const [showGuia, setShowGuia] = useState(false);
    const [editandoId, setEditandoId] = useState(null);
    
    // Estados para o Modal de Exclusão
    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const [grupoParaExcluir, setGrupoParaExcluir] = useState(null);

    const initialState = {
        nome: '', 
        tipo: 'obrigatorio', 
        minimo: 1, 
        maximo: 1,
        metodo_preco: 'soma', 
        itens: [] 
    };
    const [novoGrupo, setNovoGrupo] = useState(initialState);

    const carregarDados = async () => {
        if (!estabelecimentoId) return;
        try {
            const { data } = await api.get(`/cardapio/adicionais/${estabelecimentoId}`);
            const formatados = data.map(g => ({
                ...g,
                itens: typeof g.itens_json === 'string' ? JSON.parse(g.itens_json) : (g.itens_json || [])
            }));
            setGrupos(formatados.sort((a, b) => a.ordem - b.ordem));
        } catch (e) { 
            toast.error("Erro ao carregar adicionais."); 
        } finally { 
            setLoading(false); 
        }
    };

    useEffect(() => { carregarDados(); }, [estabelecimentoId]);

    const onDragEnd = async (result) => {
        const { destination, source } = result;
        if (!destination || (destination.droppableId === source.droppableId && destination.index === source.index)) {
            return;
        }

        const novaLista = Array.from(grupos);
        const itensMesmoTipo = novaLista.filter(g => g.tipo === source.droppableId);
        const [reorderedItem] = itensMesmoTipo.splice(source.index, 1);
        itensMesmoTipo.splice(destination.index, 0, reorderedItem);

        const outrosItens = novaLista.filter(g => g.tipo !== source.droppableId);
        const listaFinal = [...outrosItens, ...itensMesmoTipo];
        
        setGrupos(listaFinal);

        try {
            const ordens = itensMesmoTipo.map((g, index) => ({ id: g.id, ordem: index }));
            await api.post(`/cardapio/adicionais/reordenar`, { ordens });
            toast.success("Ordem atualizada!");
        } catch (e) {
            toast.error("Falha ao salvar ordem.");
            carregarDados();
        }
    };

    const handleSalvar = async (e) => {
        e.preventDefault();

        const min = parseInt(novoGrupo.minimo) || 0;
        const max = parseInt(novoGrupo.maximo) || 0;

        if (novoGrupo.itens.length === 0) {
            return toast.error("Adicione pelo menos uma opção ao grupo!");
        }

        if (max < min) {
            return toast.error("O máximo não pode ser menor que o mínimo!");
        }

        if (novoGrupo.tipo === 'obrigatorio' && min < 1) {
            return toast.error("Grupos Obrigatórios precisam de Mínimo 1.");
        }

        try {
            const payload = { 
                ...novoGrupo, 
                minimo: min,
                maximo: max,
                estabelecimento_id: estabelecimentoId,
                itens_json: JSON.stringify(novoGrupo.itens) 
            };

            if (editandoId) await api.put(`/cardapio/adicionais/${editandoId}`, payload);
            else await api.post('/cardapio/adicionais', payload);
            
            fecharModal();
            carregarDados();
            toast.success("Salvo com sucesso!");
        } catch (e) { 
            toast.error("Erro ao salvar."); 
        }
    };

    const handleExcluirGrupo = async () => {
        if (!grupoParaExcluir) return;
        try {
            await api.delete(`/cardapio/adicionais/${grupoParaExcluir.id}`);
            toast.success("Grupo removido!");
            setShowDeleteModal(false);
            setGrupoParaExcluir(null);
            carregarDados();
        } catch (e) {
            toast.error("Erro ao excluir grupo.");
        }
    };

    const adicionarItemAoGrupo = () => {
        setNovoGrupo({ ...novoGrupo, itens: [...novoGrupo.itens, { nome: '', preco: '0' }] });
    };

    const atualizarItem = (index, campo, valor) => {
        const novosItens = [...novoGrupo.itens];
        novosItens[index][campo] = valor;
        setNovoGrupo({ ...novoGrupo, itens: novosItens });
    };

    const removerItem = (index) => {
        const novosItens = novoGrupo.itens.filter((_, i) => i !== index);
        setNovoGrupo({ ...novoGrupo, itens: novosItens });
    };

    const abrirEdicao = (grupo) => {
        setEditandoId(grupo.id);
        setNovoGrupo({ 
            nome: grupo.nome,
            tipo: grupo.tipo,
            minimo: grupo.minimo,
            maximo: grupo.maximo,
            metodo_preco: grupo.metodo_preco || 'soma',
            itens: grupo.itens || [] 
        });
        setShowModal(true);
    };

    const fecharModal = () => {
        setShowModal(false);
        setEditandoId(null);
        setNovoGrupo(initialState);
        setShowGuia(false);
    };

    const renderColumn = (titulo, tipoId, icone, cor) => (
        <Droppable droppableId={tipoId}>
            {(provided) => (
                <div className="flow-column-admin" {...provided.droppableProps} ref={provided.innerRef}>
                    <div className="column-header-admin" style={{ borderLeft: `4px solid ${cor}` }}>
                        <div className="header-label">
                            {icone} <span>{titulo}</span>
                        </div>
                        <button type="button" className="btn-add-mini" onClick={() => { setNovoGrupo({...initialState, tipo: tipoId}); setShowModal(true); }}>
                            <Plus size={16} />
                        </button>
                    </div>
                    
                    <div className="column-body-admin">
                        {grupos.filter(g => g.tipo === tipoId).map((grupo, index) => (
                            <Draggable key={grupo.id.toString()} draggableId={grupo.id.toString()} index={index}>
                                {(provided) => (
                                    <div 
                                        className="flow-card-admin" 
                                        ref={provided.innerRef}
                                        {...provided.draggableProps}
                                    >
                                        <div className="card-top">
                                            <div {...provided.dragHandleProps} style={{paddingRight: '10px', display: 'flex', alignItems: 'center'}}>
                                                <GripVertical size={16} color="#cbd5e1" />
                                            </div>
                                            <div onClick={() => abrirEdicao(grupo)} style={{flex: 1, cursor: 'pointer'}}>
                                                <strong style={{display: 'block'}}>{grupo.nome}</strong>
                                                <small style={{fontSize: '10px', color: cor, fontWeight: 'bold'}}>
                                                    {grupo.metodo_preco === 'substitui' ? 'FIXO' : grupo.metodo_preco === 'maior_valor' ? 'PIZZA' : 'SOMA'}
                                                </small>
                                            </div>
                                            <div style={{display:'flex', gap:'8px', alignItems:'center'}}>
                                                <span className="badge-qty">{grupo.minimo}-{grupo.maximo}</span>
                                                <button onClick={() => { setGrupoParaExcluir(grupo); setShowDeleteModal(true); }} style={{background:'none', border:'none', cursor:'pointer', color:'#ef4444'}}>
                                                    <Trash2 size={16}/>
                                                </button>
                                            </div>
                                        </div>
                                    </div>
                                )}
                            </Draggable>
                        ))}
                        {provided.placeholder}
                    </div>
                </div>
            )}
        </Droppable>
    );

    if (loading) return <div className="loader-container"><div className="spinner"></div></div>;

    return (
        <div className="tab-content animate-fade-in">
            <Toaster position="top-right" />
            <div className="top-info" style={{ marginBottom: '25px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <div>
                    <h2 className="content-title" style={{margin: 0}}>Complementos e Sabores</h2>
                    <p style={{ color: '#64748b', fontSize: '14px' }}>Arraste para ordenar ou configure as regras de cada grupo.</p>
                </div>
                <button type="button" onClick={() => setShowGuia(!showGuia)} className="btn-help-circle">
                    <HelpCircle size={24} color={showGuia ? '#3b82f6' : '#64748b'} />
                </button>
            </div>

            {showGuia && (
                <div className="guia-regra-box" style={{background: '#f0f9ff', padding: '15px', borderRadius: '10px', border: '1px solid #bae6fd', marginBottom: '20px', fontSize: '13px'}}>
                    <strong>🍱 Guia Rápido:</strong>
                    <ul style={{marginTop: '5px', color: '#0369a1'}}>
                        <li><b>Substituir (=):</b> Para Marmitex. O preço do item escolhido vira o preço total.</li>
                        <li><b>Maior Valor:</b> Para Pizzas Meio-a-Meio. Cobra apenas o sabor mais caro.</li>
                    </ul>
                </div>
            )}

            <DragDropContext onDragEnd={onDragEnd}>
                <div className="flow-columns-grid" style={{display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '20px'}}>
                    {renderColumn("Obrigatórios", "obrigatorio", <Settings2 size={18} color="#ef4444"/>, "#ef4444")}
                    {renderColumn("Opcionais", "adicional", <Plus size={18} color="#3b82f6"/>, "#3b82f6")}
                    {renderColumn("Informativos", "informativo", <Info size={18} color="#64748b"/>, "#64748b")}
                </div>
            </DragDropContext>

            {/* MODAL DE CADASTRO/EDIÇÃO */}
            {showModal && (
                <div className="modal-overlay" style={{zIndex: 2500}}>
                    <div className="modal" style={{ maxWidth: '650px', width: '90%', maxHeight: '90vh', overflowY: 'auto' }}>
                        <div className="modal-header-flex">
                            <h2 style={{margin: 0}}>{editandoId ? '⚙️ Editar Grupo' : '✨ Novo Grupo'}</h2>
                            <button onClick={fecharModal} className="btn-close-modal-x"><X size={24}/></button>
                        </div>

                        <form onSubmit={handleSalvar} style={{ marginTop: '20px' }}>
                            <label className="label">Nome do Grupo</label>
                            <input className="input" value={novoGrupo.nome} onChange={e => setNovoGrupo({...novoGrupo, nome: e.target.value})} required placeholder="Ex: Escolha o sabor..." />

                            <div style={{ display: 'grid', gridTemplateColumns: '1fr 0.6fr 0.6fr 1fr', gap: '10px', marginTop: '15px' }}>
                                <div>
                                    <label className="label">Tipo</label>
                                    <select className="input" value={novoGrupo.tipo} onChange={e => setNovoGrupo({...novoGrupo, tipo: e.target.value})}>
                                        <option value="obrigatorio">Obrigatório</option>
                                        <option value="adicional">Opcional</option>
                                        <option value="informativo">Informativo</option>
                                    </select>
                                </div>
                                <div><label className="label">Mín</label><input type="number" className="input" value={novoGrupo.minimo} onChange={e => setNovoGrupo({...novoGrupo, minimo: e.target.value})} /></div>
                                <div><label className="label">Máx</label><input type="number" className="input" value={novoGrupo.maximo} onChange={e => setNovoGrupo({...novoGrupo, maximo: e.target.value})} /></div>
                                <div>
                                    <label className="label">Cálculo</label>
                                    <select className="input" value={novoGrupo.metodo_preco} onChange={e => setNovoGrupo({...novoGrupo, metodo_preco: e.target.value})}>
                                        <option value="soma">Soma (+)</option>
                                        <option value="substitui">Substitui (=)</option>
                                        <option value="maior_valor">Maior Valor</option>
                                    </select>
                                </div>
                            </div>

                            <div className="items-editor-section" style={{ marginTop: '25px' }}>
                                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '10px' }}>
                                    <h4 style={{margin:0}}><ListPlus size={18}/> Opções</h4>
                                    <button type="button" onClick={adicionarItemAoGrupo} className="btn-add-item-modal">+ Item</button>
                                </div>
                                <div style={{ maxHeight: '250px', overflowY: 'auto' }}>
                                    {novoGrupo.itens.map((item, index) => (
                                        <div key={index} style={{ display: 'flex', gap: '10px', marginBottom: '8px' }}>
                                            <input className="input" placeholder="Nome" value={item.nome} onChange={e => atualizarItem(index, 'nome', e.target.value)} style={{ flex: 2 }} required />
                                            <input className="input" placeholder="R$ 0,00" value={item.preco} onChange={e => atualizarItem(index, 'preco', e.target.value)} style={{ flex: 1 }} />
                                            <button type="button" onClick={() => removerItem(index)} className="btn-delete-item-modal"><Trash2 size={18}/></button>
                                        </div>
                                    ))}
                                </div>
                            </div>

                            <div className="modal-buttons" style={{marginTop: '30px'}}>
                                <button type="button" onClick={fecharModal} className="btn-cancel">Cancelar</button>
                                <button type="submit" className="btn-save-confirm" style={{flex: 1}}>Salvar Grupo</button>
                            </div>
                        </form>
                    </div>
                </div>
            )}

            {/* MODAL DE CONFIRMAÇÃO DE EXCLUSÃO */}
            {showDeleteModal && (
                <div className="modal-overlay" style={{zIndex: 3000}}>
                    <div className="modal-confirm" style={{background: '#fff', padding: '30px', borderRadius: '15px', maxWidth: '400px', textAlign: 'center'}}>
                        <AlertTriangle size={48} color="#ef4444" style={{marginBottom: '15px'}} />
                        <h3 style={{margin: '0 0 10px 0'}}>Excluir Grupo?</h3>
                        <p style={{fontSize: '14px', color: '#64748b'}}>Você está prestes a remover o grupo <b>{grupoParaExcluir?.nome}</b>. Esta ação não pode ser desfeita.</p>
                        <div className="modal-buttons" style={{marginTop: '25px', display: 'flex', gap: '10px', justifyContent: 'center'}}>
                            <button onClick={() => setShowDeleteModal(false)} className="btn-cancel">Voltar</button>
                            <button onClick={handleExcluirGrupo} className="btn-confirm-delete" style={{background: '#ef4444', color: '#fff', border: 'none', padding: '10px 20px', borderRadius: '8px', fontWeight: 'bold'}}>Confirmar Exclusão</button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default Adicionais;